var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useDashboardTheme } from "feature/dashboard";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
/**
 * Custom Hook to generate Chart.js Bar options based on mode and container ID.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @returns {object} The complete Chart.js options object.
 */
export function useBarChart(containerId) {
    // Use useMemo to ensure the options object is only re-created
    // when the dependencies (containerId) change.
    var options = useMemo(function () {
        // --- 1. Define Base Configuration ---
        var baseCommonOptions = {
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                    ticks: {
                        stepSize: 400000,
                        display: false,
                    },
                },
            },
        };
        var basePlugins = {
            legend: {
                display: false,
            },
            htmlLegend: {
                // ID of the container to put the legend in
                containerID: containerId,
            },
            tooltip: {
                mode: "index",
                interaction: { intersect: false },
            },
        };
        // --- 2. Create Initial Options Object (Full Copy) ---
        var finalOptions = __assign(__assign({}, baseCommonOptions), { plugins: __assign({}, basePlugins) });
        return finalOptions;
    }, [containerId]);
    var _a = useDashboardTheme(), isDefaultStyle = _a.isDefaultStyle, primaryColor = _a.primaryColor, primaryColor_05 = _a.primaryColor_05;
    var successColorCode = isDefaultStyle ? "#77D3E1" : primaryColor;
    var failureColorCode = isDefaultStyle ? "#706CF9" : primaryColor_05;
    return {
        BarChart: function (_a) {
            var data = _a.data;
            var modifiedData = __assign(__assign({}, data), { datasets: data.datasets.map(function (ds) {
                    var _a, _b, _c, _d;
                    return __assign(__assign({}, ds), { backgroundColor: (_a = ds.backgroundColor) !== null && _a !== void 0 ? _a : successColorCode, borderColor: (_b = ds.borderColor) !== null && _b !== void 0 ? _b : successColorCode, borderWidth: (_c = ds.borderWidth) !== null && _c !== void 0 ? _c : 0, barThickness: (_d = ds.barThickness) !== null && _d !== void 0 ? _d : 20 });
                }) });
            return (_jsx(Bar, { data: modifiedData, options: options, plugins: [getCustomLegend()] }));
        },
        BarChartFooter: function (_a) {
            var className = _a.className;
            return (_jsx("div", { id: containerId, className: cn("mt-8", className) }));
        },
    };
}
